/**
 * Phase 7 · `.wizard` step navigation handler.
 * Ports `initWizards()` from `01_BUILD/qa_studio.html` L22794.
 *
 * Per Genie ack 2 MAY 2026 (#3=persisted) · active step is persisted via
 * `setStep(wizardId, step)` callback to `progress.tickets[id].wizardState`.
 *
 * Markup contract (per qa_studio.html):
 *   <div class="wizard" data-wizard-id="<unique-id>">
 *     <div class="wiz-progress">
 *       <div class="wiz-bar"><span class="wiz-fill"></span></div>
 *       <div class="wiz-counter">
 *         <span class="wiz-current">1</span> / <span class="wiz-total">N</span>
 *       </div>
 *     </div>
 *     <div class="wiz-stage active" data-stage-idx="0">…</div>
 *     <div class="wiz-stage" data-stage-idx="1">…</div>
 *     ...
 *     <div class="wiz-controls">
 *       <button class="wiz-btn wiz-prev">←</button>
 *       <button class="wiz-btn wiz-next">→</button>
 *     </div>
 *   </div>
 */

interface WizardSetStep {
  (wizardId: string, step: number): void;
}

export function initWizards(root: HTMLElement, setStep: WizardSetStep): () => void {
  const wizards = Array.from(root.querySelectorAll<HTMLElement>('.wizard'));
  const cleanups: Array<() => void> = [];

  for (const wiz of wizards) {
    if (wiz.dataset.wizardInit === '1') continue;
    wiz.dataset.wizardInit = '1';
    const wizardId = wiz.dataset.wizardId ?? wiz.id ?? `wizard-${Math.random().toString(36).slice(2, 8)}`;
    if (!wiz.dataset.wizardId) wiz.dataset.wizardId = wizardId;

    const stages = Array.from(wiz.querySelectorAll<HTMLElement>('.wiz-stage'));
    const total = stages.length;
    const prevBtn = wiz.querySelector<HTMLButtonElement>('.wiz-prev');
    const nextBtn = wiz.querySelector<HTMLButtonElement>('.wiz-next');

    const goTo = (n: number) => {
      const clamped = Math.max(0, Math.min(total - 1, n));
      setStep(wizardId, clamped);
      // DOM sync also happens via syncWizardDom on re-render, but apply
      // immediately for snappy response when state hasn't roundtripped yet.
      applyWizardStage(wiz, clamped, total);
    };

    const onPrev = () => {
      const cur = getActiveStageIdx(wiz);
      goTo(cur - 1);
    };
    const onNext = () => {
      const cur = getActiveStageIdx(wiz);
      goTo(cur + 1);
    };

    prevBtn?.addEventListener('click', onPrev);
    nextBtn?.addEventListener('click', onNext);

    cleanups.push(() => {
      prevBtn?.removeEventListener('click', onPrev);
      nextBtn?.removeEventListener('click', onNext);
      delete wiz.dataset.wizardInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}

function getActiveStageIdx(wiz: HTMLElement): number {
  const stages = Array.from(wiz.querySelectorAll<HTMLElement>('.wiz-stage'));
  const active = stages.findIndex((s) => s.classList.contains('active'));
  return active >= 0 ? active : 0;
}

function applyWizardStage(wiz: HTMLElement, idx: number, total: number) {
  const stages = Array.from(wiz.querySelectorAll<HTMLElement>('.wiz-stage'));
  stages.forEach((s, i) => s.classList.toggle('active', i === idx));
  const fill = wiz.querySelector<HTMLElement>('.wiz-fill');
  if (fill && total > 0) {
    fill.style.width = `${((idx + 1) / total) * 100}%`;
  }
  const current = wiz.querySelector<HTMLElement>('.wiz-current');
  if (current) current.textContent = String(idx + 1);
  const totalEl = wiz.querySelector<HTMLElement>('.wiz-total');
  if (totalEl && totalEl.textContent !== String(total)) totalEl.textContent = String(total);
  const prev = wiz.querySelector<HTMLButtonElement>('.wiz-prev');
  const next = wiz.querySelector<HTMLButtonElement>('.wiz-next');
  if (prev) prev.disabled = idx <= 0;
  if (next) next.disabled = idx >= total - 1;
}

/**
 * Re-apply persisted wizard state to DOM (called from ContentRenderer after
 * every render so reload / state-update reflects in UI).
 */
export function syncWizardDom(
  root: HTMLElement,
  wizardState: Record<string, { activeStep: number }>,
): void {
  const wizards = Array.from(root.querySelectorAll<HTMLElement>('.wizard'));
  for (const wiz of wizards) {
    const wizardId = wiz.dataset.wizardId;
    if (!wizardId) continue;
    const total = wiz.querySelectorAll('.wiz-stage').length;
    const persisted = wizardState[wizardId]?.activeStep;
    const idx = persisted !== undefined ? persisted : getActiveStageIdx(wiz);
    applyWizardStage(wiz, Math.max(0, Math.min(total - 1, idx)), total);
  }
}
